import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Trash2, Wand2, Play, ArrowRight, RotateCcw, Ban, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';
import { formatEastern } from '@/lib/format';
import { useConfirm } from '@/components/ConfirmDialog';
import { prepChip, timeLeft, orderName, EDITOR_MESSAGE, CLIENT_LABEL } from '@/lib/autoprep';

const TONE = {
  teal: 'text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/10',
  amber: 'text-amber-300 border-amber-400/30 bg-amber-400/10',
  red: 'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10',
  grey: 'text-slate-400 border-slate-600/40 bg-white/[0.03]',
};
const STEP_TONE = { waiting: 'grey', downloading: 'teal', prepping: 'teal', uploading: 'teal', ready: 'teal', failed: 'red', given_up: 'grey' };

/** The board row's chip (R2): the team's "Auto-prep pending" with the step, an editor's sentence, a client's label. */
export function AutoPrepChip({ project, role, idSuffix = '' }) {
  const c = prepChip(project, role);
  if (!c) return null;
  return (
    // The editor's is a whole sentence (R2) and must be read whole, so it wraps inside the column; the short ones do not.
    <span data-testid={`autoprep-chip-${c.kind}${idSuffix}`} title={c.title}
      data-tone={c.tone === 'amber' ? 'warning' : undefined}
      className={`tap-auto inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 border ${
        c.kind === 'editor' ? 'max-w-full whitespace-normal leading-snug rounded-lg' : 'shrink-0 whitespace-nowrap rounded-full'} ${TONE[c.tone]}`}>
      <Wand2 className="w-2.5 h-2.5 shrink-0" /> {c.label}
    </span>
  );
}

/** Under the drawer's header, for the people who are told only that the order is being prepared (R2). */
export function AutoPrepNotice({ project, role }) {
  if (!project?.autoprep?.held || (role !== 'editor' && role !== 'client')) return null;
  return (
    <p data-testid={`autoprep-notice-${role}`}
      className="tap-auto mt-2 inline-flex items-start gap-1.5 text-[11px] leading-relaxed rounded-md border px-2.5 py-1.5 text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/[0.07]">
      <Wand2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {role === 'editor' ? EDITOR_MESSAGE : CLIENT_LABEL}
    </p>
  );
}

// What happened, said for the card (docs/autoprep/QUEUE.md section 9). One sentence, the one that matters now.
function situation(job) {
  const when = (iso) => formatEastern(iso);
  if (job.state === 'failed') {
    const release = job.actions?.includes('release') ? '; Release to editor sends the order on without the prep' : '';
    return `${job.error?.message || 'It stopped without saying why.'} Resume tries again from where it stopped${release}.`;
  }
  // one order of a wedding whose product was not in the footage (a Speeches Only order with no speeches): the rest was made
  if (job.nothing_reason && (job.state === 'uploading' || job.state === 'ready')) {
    return `Not prepared: ${job.nothing_reason}. The editor downloads the footage as usual; the rest of the wedding was prepared.`;
  }
  if (job.state === 'ready') return job.footage_deleted_at ? `Ready. The footage was removed from the prep computer on ${when(job.footage_deleted_at)}.` : 'Ready.';
  if (job.state === 'given_up') return 'Given up. The order moves on without the prep.';
  if (job.stalled) return `The prep computer stopped checking in${job.last_seen_at ? ` on ${when(job.last_seen_at)}` : ''}. It carries on by itself when the computer is back.`;
  if (job.hold_released_at) {
    // the time limit's release ("twelve_hours" on records from before R25, when the limit was 12 hours)
    const who = job.hold_release_reason === 'twelve_hours' ? 'after 12 hours'
      : job.hold_release_reason === 'time_limit' ? `after ${job.hold_hours || 'its'} hours` : `by ${job.hold_released_by_name || 'a person'}`;
    return `Released to the editor ${who} on ${when(job.hold_released_at)}. The prep carries on; if it finishes, Footage Pull adds it to the editor's next download.`;
  }
  if (job.held && job.at_assigned) return `The editor cannot download the footage until this is ready. If it is not ready in ${timeLeft(job.release_at)}, the download opens without it.`;
  if (job.held) return `The order stays at Project Requested until this is ready. If it is not ready in ${timeLeft(job.release_at)}, the order moves on without it.`;
  if (job.state === 'waiting') {
    if (!job.place) return job.waiting_reason || 'Waiting for the prep computer to start it.';
    return `Number ${job.place} in line (${job.line}).${job.waiting_reason ? ` ${job.waiting_reason}.` : ''}`;
  }
  if (!job.at_requested && !job.at_assigned) return 'Nothing waits for this: the editor is already working on the project.';
  return 'Watching only: the order moves on as usual while this runs.';
}

// Admin's start by hand (QUEUE.md R14), said for the three places the card offers it: no prep yet, a prep that
// ended, and one still waiting in line. A wedding is prepared in one run (R18), so each says what that means here.
function startAsk(job, { queueOn, wouldHold, hasPackage }) {
  const off = queueOn ? '' : ' Auto-prep is switched off in Settings, so it starts once it is switched on.';
  const hold = wouldHold ? ' The editor cannot download the footage until the prep is ready (at most 72 hours at Project Requested, 36 at Assigned to Editor).' : '';
  const keeps = hasPackage ? ' The editor keeps getting the current one until the new one is ready.' : '';
  if (job?.state === 'waiting') {
    return { title: 'Prepare this project next?', message: `It moves to the front of the line with the rest of its wedding. Nothing else changes.${off}`,
      confirmLabel: 'Prep next', destructive: false, done: 'Moved to the front of the line' };
  }
  if (job) {
    const others = job.other_orders?.length ? ' The other orders of its wedding are made again with it if they would join auto-prep on their own.' : '';
    return { title: 'Prepare this project again?',
      message: `Everything is made again from the start, including the AI steps (about a dollar), and the footage is downloaded again. It goes next.${others}${keeps}${hold}${off}`,
      confirmLabel: 'Prep again', destructive: false, done: 'Auto-prep starts again' };
  }
  return { title: 'Start auto-prep for this project?',
    message: `The prep computer downloads the footage and prepares the editing project. It goes next. If the same wedding is being prepared now, or was in the last month, this one is made from that prep.${keeps}${hold}${off}`,
    confirmLabel: 'Start auto-prep', destructive: false, done: 'Auto-prep started' };
}

// Retry and Give up act on the wedding's whole run (R18): said so when the run makes other orders too.
function wholeRun(job, a) {
  const names = (job.other_orders || []).map(o => o.product || 'another order').join(', ');
  if (!names) return ASK[a];
  if (a === 'retry') return { ...ASK.retry, message: `${ASK.retry.message} The rest of its wedding (${names}) is made again with it.` };
  if (a === 'give-up') {
    return { ...ASK['give-up'], message: `The prep stops for good, for the rest of its wedding too (${names}), and the footage it downloaded is deleted from the prep computer. Each order moves on without it. This cannot be undone.` };
  }
  return ASK[a];
}

const ASK = {
  resume: {
    title: 'Resume auto-prep?',
    message: 'The prep computer carries on from where it stopped: it keeps the footage it downloaded and every step it finished.',
    confirmLabel: 'Resume', destructive: false, done: 'Auto-prep resumed',
  },
  release: {
    title: 'Release this order to the editor now?',
    message: 'The order moves on now without the prep, the same way it would without auto-prep, and the "Auto-prep pending" label goes. The prep keeps running: if it finishes, Footage Pull adds it to the editor\'s next download.',
    confirmLabel: 'Release to editor', destructive: true, done: 'Released to the editor',
  },
  retry: {
    title: 'Start auto-prep over?',
    message: 'Everything the prep made for this order is thrown away and made again from the start, including the AI steps (about a dollar). The footage stays on the prep computer, so nothing is downloaded twice.',
    confirmLabel: 'Start over', destructive: true, done: 'Auto-prep starts over',
  },
  'give-up': {
    title: 'Give up on auto-prep for this order?',
    message: 'The prep stops for good and the footage it downloaded is deleted from the prep computer. The order moves on without it. This cannot be undone.',
    confirmLabel: 'Give up', destructive: true, done: 'Auto-prep given up',
  },
};
// The start button's label is its confirm label: "Prep next" or "Prep again" (startAsk).
const BUTTONS = [['resume', 'Resume', Play], ['release', 'Release to editor', ArrowRight], ['retry', 'Retry', RotateCcw], ['give-up', 'Give up', Ban], ['start', '', Wand2]];
const BUTTON_CLASS = 'tap-auto inline-flex items-center gap-1 text-[10px] font-medium border border-ug-border rounded px-2 py-1.5 transition-colors disabled:opacity-40';

// One press of a card button: ask first (the existing confirm dialog, saying what will happen), send it, read the card again.
function usePress(project, onChange) {
  const [busy, setBusy] = useState('');
  const confirm = useConfirm();
  const press = async (action, a) => {
    if (!(await confirm({ title: a.title, message: a.message, confirmLabel: a.confirmLabel, cancelLabel: 'Not now', destructive: a.destructive }))) return;
    setBusy(action);
    try {
      await api.post(action === 'start' ? `/projects/${project.id}/autoprep/start` : `/projects/${project.id}/autoprep/job/${action}`);
      toast.success(a.done);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'That did not go through. Close and reopen the project, then try again.'); }
    finally { setBusy(''); }
  };
  return [busy, press];
}

function JobPart({ project, user, job, events, hasPackage, onChange }) {
  const [busy, press] = usePress(project, onChange);
  const [history, setHistory] = useState(false);
  const flags = { queueOn: job.queue_switch, wouldHold: job.at_requested && job.hold_switch, hasPackage };
  const pct = job.state === 'downloading' ? job.progress?.pct : null;
  return (
    <div data-testid="autoprep-job" className="mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="autoprep-job-step"
          className={`tap-auto inline-flex items-center font-sans text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TONE[job.stalled ? 'amber' : STEP_TONE[job.state] || 'grey']}`}>
          {job.step}
        </span>
        {job.held && <span data-testid="autoprep-job-held" className="text-[10px] text-slate-400">Order held</span>}
        {job.progress?.detail && <span className="text-[10px] text-slate-500">{job.progress.detail}</span>}
      </div>
      {pct != null && (
        <div className="mt-2 h-1 rounded-full bg-white/[0.06] overflow-hidden" aria-hidden="true">
          <div className="h-full bg-oxidized-teal" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
        </div>
      )}
      <p data-testid="autoprep-job-situation" className="text-[11px] text-zinc-300 mt-2 leading-relaxed">{situation(job)}</p>
      {job.other_orders?.length > 0 && (
        <p data-testid="autoprep-job-wedding" className="text-[10px] text-slate-400 mt-1 leading-relaxed">
          Made in one run with this wedding&apos;s{' '}
          {job.other_orders.map((o, i) => (
            <span key={o.project_id}>
              {i > 0 && ', '}
              <a href={`/dashboard?open=${o.project_id}&tab=files`} className="tap-auto text-slate-300 hover:text-ug-gold underline underline-offset-2">{orderName(o, project.title)}</a>
            </span>
          ))}: one download, and a project for each order.
        </p>
      )}
      {job.ai_cost_usd != null && (
        <p data-testid="autoprep-job-ai-cost" className="text-[10px] text-slate-500 mt-1">
          The AI steps cost ${job.ai_cost_usd.toFixed(2)} for this {job.other_orders?.length ? 'wedding, all its orders together' : 'order'}.
        </p>
      )}
      {job.actions?.length > 0 && (
        <div className="flex gap-2 mt-2.5 flex-wrap">
          {BUTTONS.filter(([a]) => job.actions.includes(a.replace('-', '_'))).map(([a, label, Icon]) => {
            const ask = a === 'start' ? startAsk(job, flags) : wholeRun(job, a);
            return (
              <button key={a} type="button" disabled={!!busy} onClick={() => press(a, ask)} data-testid={`autoprep-job-${a}`}
                className={`${BUTTON_CLASS} ${ask.destructive ? 'text-slate-400 hover:text-ug-status-red' : 'text-slate-300 hover:text-zinc-100'}`}>
                <Icon className="w-3 h-3" /> {busy === a ? 'Working…' : label || ask.confirmLabel}
              </button>
            );
          })}
        </div>
      )}
      {events?.length > 0 && (
        <div className="mt-2">
          <button type="button" onClick={() => setHistory(v => !v)} data-testid="autoprep-job-history-toggle"
            className="tap-auto inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-zinc-100 transition-colors">
            {history ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} {history ? 'Hide what happened' : 'What happened'}
          </button>
          {history && (
            <ul data-testid="autoprep-job-history" className="mt-1.5 space-y-1">
              {events.slice(0, 12).map(e => (
                <li key={e.id} className="text-[10px] text-slate-400 leading-snug">
                  <span className="text-slate-500">{formatEastern(e.at)}</span> · {e.reason || e.event}{e.user_name && !e.user_id?.startsWith('system:') ? ` (${e.user_name})` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {user.role === 'staff' && job.state === 'failed' && (
        <p className="text-[10px] text-slate-500 mt-2">Retry and Give up are an admin&apos;s.</p>
      )}
    </div>
  );
}

// No prep now: why the order left the line if it did (R17, R18), then admin's start by hand (QUEUE.md R14), for a
// video order of a product auto-prep prepares.
function StartPart({ project, start, hasPackage, onChange }) {
  const [busy, press] = usePress(project, onChange);
  const ask = startAsk(null, { queueOn: start.queue_switch, wouldHold: start.would_hold, hasPackage });
  return (
    <div data-testid="autoprep-start-part" className="mt-2">
      {start.dropped && (
        <p data-testid="autoprep-dropped" className="text-[11px] text-zinc-300 leading-relaxed">
          {start.dropped.reason} <span className="text-slate-500">({formatEastern(start.dropped.at)})</span>
        </p>
      )}
      {!hasPackage && !start.dropped && <p className="text-[11px] text-zinc-300 leading-relaxed">Auto-prep has not prepared this project yet.</p>}
      {/* Nothing to go and get yet (rules.start_blocked): said in the server's words, where the button would be. */}
      {start.start_blocked && (
        <p data-testid="autoprep-start-blocked" className="mt-1.5 text-[11px] text-slate-400 leading-relaxed">{start.start_blocked}</p>
      )}
      {start.can_start && (
        <div className="flex gap-2 mt-2.5">
          <button type="button" disabled={!!busy} onClick={() => press('start', ask)} data-testid="autoprep-start"
            className={`${BUTTON_CLASS} text-slate-300 hover:text-zinc-100`}>
            <Wand2 className="w-3 h-3" /> {busy ? 'Working…' : ask.confirmLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The drawer's Auto-prep card, Files tab, admin and staff (backend routes/autoprep_queue.py and routes/autoprep.py;
 * docs/autoprep/QUEUE.md section 9). While a prep job exists: its step, its wedding's place in the line while it waits,
 * the wedding's other orders made in the same run (R18), how long before the hold's time limit, and the buttons, each
 * asking first. With none: why the order left the line if it did, and admin can start one. Once the prep
 * computer has uploaded a package: what it is, Download, and Remove for admin. Reading the package here hands out no
 * link (`link=false`); Download asks for one.
 */
export function AutoPrepCard({ project, user }) {
  const [pkg, setPkg] = useState(null);
  const [job, setJob] = useState(null);
  const [start, setStart] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState(null);         // {wedding_type, wedding_types}: what kind of wedding (R27)
  const confirm = useConfirm();

  const load = useCallback(() => {
    api.get(`/projects/${project.id}/autoprep`, { params: { link: false } })
      .then(r => setPkg(r.data?.ready ? r.data : null))
      .catch(() => setPkg(null));
    api.get(`/projects/${project.id}/autoprep/job`)
      .then(r => {
        setJob(r.data?.job || null); setStart(r.data?.job ? null : r.data); setEvents(r.data?.events || []);
        setKind(r.data?.wedding_types ? { wedding_type: r.data.wedding_type, wedding_types: r.data.wedding_types } : null);
      })
      .catch(() => { setJob(null); setStart(null); });
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  // The step moves while the drawer is open (a download's percent, a stall): look again every 15 seconds until done.
  const unfinished = job && !['ready', 'given_up'].includes(job.state);
  useEffect(() => {
    if (!unfinished) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [unfinished, load]);

  const canStart = !job && start?.can_start;
  const leftLine = !job && !!start?.dropped;      // why the order left the line, for staff too (R17, R18)
  const blocked = !job && !!start?.start_blocked;  // nothing to go and get yet: the card says why, for staff too
  if (!pkg && !job && !canStart && !leftLine && !blocked) return null;

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/projects/${project.id}/autoprep`);
      if (!r.data?.url) throw new Error('no link');
      window.location.assign(r.data.url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not get the auto-prep download. Try again.'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Remove the auto-prep project?',
      message: 'It is deleted from storage, so Footage Pull stops setting it up for the editor. An editor who already has it keeps their copy. To bring it back, upload it again from the prep computer.',
      confirmLabel: 'Remove', cancelLabel: 'Keep it', destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${project.id}/autoprep`);
      toast.success('Auto-prep project removed');
      setPkg(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove the auto-prep project. Try again.'); }
    finally { setBusy(false); }
  };

  // What kind of wedding (Ryan, 2026-10-09): the prep tells every AI step, and offers that kind's events. Read when a
  // prep starts, so one already under way keeps what it started with until Retry.
  const setWeddingType = async (value) => {
    const was = kind?.wedding_type;
    setKind(k => ({ ...k, wedding_type: value }));
    try {
      const r = await api.put(`/projects/${project.id}/autoprep/wedding-type`, { wedding_type: value });
      setKind(k => ({ ...k, wedding_type: r.data.wedding_type }));
      const label = (kind?.wedding_types || []).find(([v]) => v === value)?.[1] || value;
      toast.success(job && !['waiting'].includes(job.state)
        ? `Saved as ${label}. The prep already under way keeps what it started with: Retry to prepare it again as ${label}.`
        : `Saved as ${label}.`);
    } catch (e) {
      setKind(k => ({ ...k, wedding_type: was }));
      toast.error(e.response?.data?.detail || 'That did not save. Try again.');
    }
  };

  return (
    <div data-testid="autoprep-card" className="rounded-xl bg-ug-raised border border-ug-border px-4 py-3">
      <div className="flex items-center gap-2">
        <Wand2 className="w-3.5 h-3.5 text-oxidized-teal" />
        <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-400">Auto-prep</h3>
        {kind && (
          <label className="ml-auto flex items-center gap-1.5 text-[10px] text-slate-400">
            Wedding type
            <select value={kind.wedding_type} onChange={e => setWeddingType(e.target.value)} data-testid="autoprep-wedding-type"
              className="tap-auto ug-field rounded border border-ug-border bg-ug-surface px-1.5 py-0.5 text-[10px] text-zinc-200">
              {kind.wedding_types.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </label>
        )}
      </div>
      {job && <JobPart project={project} user={user} job={job} events={events} hasPackage={!!pkg} onChange={load} />}
      {(canStart || leftLine || blocked) && <StartPart project={project} start={start} hasPackage={!!pkg} onChange={load} />}
      {pkg && (
        <div data-testid="autoprep-package" className={job || canStart || leftLine || blocked ? 'mt-3 pt-3 border-t border-ug-border' : ''}>
          <p data-testid="autoprep-package-summary" className="text-[11px] text-zinc-300 mt-1.5">
            {[pkg.program, `${Math.max(1, Math.round(pkg.size / 1048576))} MB`, pkg.ready_at && `uploaded ${formatEastern(pkg.ready_at)}`]
              .filter(Boolean).join(' · ')}
          </p>
          <p data-testid="autoprep-package-editors" className="text-[10px] text-slate-500 mt-1">
            {pkg.editors_enabled
              ? 'Footage Pull sets it up on the editor’s computer after the footage.'
              : 'Editors do not get it yet: an admin can switch on "Give editors the prepared project" in Settings.'}
          </p>
          <div className="flex gap-2 mt-2.5">
            <button type="button" disabled={busy} onClick={download} data-testid="autoprep-package-download"
              className="tap-auto inline-flex items-center gap-1 text-[10px] font-medium text-slate-300 hover:text-zinc-100 border border-ug-border rounded px-2 py-1.5 transition-colors disabled:opacity-40">
              <Download className="w-3 h-3" /> Download
            </button>
            {user.role === 'admin' && (
              <button type="button" disabled={busy} onClick={remove} data-testid="autoprep-package-remove"
                className="tap-auto inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-ug-status-red border border-ug-border rounded px-2 py-1.5 transition-colors disabled:opacity-40">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
